import React from "react";
import { Link } from "react-router-dom";
import SEO from "./SEO";
import PageLayout from "./PageLayout";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";
import { SITE } from "../data/siteConfig";

/**
 * Wiederverwendbares Template für Service-, Branchen- und Info-Seiten.
 * Erwartet: title, metaTitle, metaDescription, path, h1, intro, sections[], faqs[], breadcrumbs[], relatedLinks[], jsonLd (optional).
 */
const ContentPageTemplate = ({
  title,
  metaTitle,
  metaDescription,
  path,
  h1,
  intro,
  sections = [],
  faqs = [],
  breadcrumbs = [],
  relatedLinks = [],
  jsonLd = null,
}) => {
  const canonicalUrl = `${SITE.url}${path}`;
  const defaultBreadcrumbs = [{ label: title }];
  const breadcrumbList = breadcrumbs.length ? breadcrumbs : defaultBreadcrumbs;

  const schema = [
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Start", item: SITE.url },
        { "@type": "ListItem", position: 2, name: title, item: canonicalUrl },
      ],
    },
    ...(faqs?.length
      ? [
          {
            "@type": "FAQPage",
            mainEntity: faqs.map(({ q, a }) => ({
              "@type": "Question",
              name: q,
              acceptedAnswer: { "@type": "Answer", text: a },
            })),
          },
        ]
      : []),
  ];
  const structuredData = jsonLd || (schema.length ? schema : null);

  return (
    <>
      <SEO
        title={metaTitle}
        description={metaDescription}
        path={path}
        structuredData={structuredData}
      />
      <PageLayout
        breadcrumbs={breadcrumbList}
        ctaTitle="Kostenlose Erstberatung"
        ctaSubtitle="Wir besprechen Ihr Projekt unverbindlich – in Wolfsberg, per Videocall oder in ganz Österreich."
      >
        <article className="max-w-3xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-bold text-[#0f1419] mb-6">
            {h1}
          </h1>
          {intro && (
            <p className="text-lg text-[#3a3a3a] leading-relaxed mb-10">
              {intro}
            </p>
          )}

          {sections?.map((section, i) => (
            <section key={i} className="mb-10">
              <h2 className="text-2xl font-semibold text-[#0f1419] mb-3">
                {section.title}
              </h2>
              <p className="text-[#3a3a3a] leading-relaxed">{section.content}</p>
            </section>
          ))}

          {faqs?.length > 0 && (
            <section className="mt-12" aria-labelledby="faq-heading">
              <h2 id="faq-heading" className="text-2xl font-semibold text-[#0f1419] mb-6">
                Häufig gestellte Fragen
              </h2>
              <Accordion type="single" collapsible className="space-y-3">
                {faqs.map((faq, i) => (
                  <AccordionItem
                    key={i}
                    value={`faq-${i}`}
                    className="bg-white border border-[#e8e4dc] rounded-xl px-5 overflow-hidden"
                  >
                    <AccordionTrigger className="text-left font-semibold text-[#0f1419] hover:text-[#4a9da8] py-4 hover:no-underline">
                      {faq.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-[#3a3a3a] pb-4">
                      {faq.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </section>
          )}

          {relatedLinks?.length > 0 && (
            <p className="mt-10 text-[#3a3a3a] flex flex-wrap gap-x-2 gap-y-1">
              {relatedLinks.map((link, i) => (
                <span key={i}>
                  {i > 0 && " · "}
                  <Link to={link.path} className="text-[#4a9da8] font-semibold hover:underline">
                    {link.label}
                  </Link>
                </span>
              ))}
            </p>
          )}
        </article>
      </PageLayout>
    </>
  );
};

export default ContentPageTemplate;
